import { useAuth } from '@/contexts/AuthContext';
import type { SubscriptionStatus } from '@/types/database';

/**
 * Helper function to check if a subscription status indicates an active PRO user
 */
export function isSubscriptionActive(status: SubscriptionStatus): boolean {
  return status === 'active' || status === 'trialing';
}

/**
 * Helper function to check if a subscription end date is in the future
 */
export function isSubscriptionDateActive(endDateStr: string | null | undefined): boolean {
  if (!endDateStr) return false;
  try {
    const endDate = new Date(endDateStr);
    return endDate > new Date();
  } catch {
    return false;
  }
}

/**
 * Hook to check if the current user is a PRO subscriber
 * Returns true if:
 * - subscription_status is 'active' or 'trialing'
 * - OR subscription_end_date is in the future (retains Pro access for cancelled subscriptions)
 * - OR plan_type is 'pro' or 'admin' (legacy/manual assignment)
 * - OR is_premium is true (legacy field)
 */
export function useIsUserPro(): boolean {
  const { profile } = useAuth();
  
  if (!profile) return false;
  
  // Check new Stripe subscription status first
  if (isSubscriptionActive(profile.subscription_status)) {
    return true;
  }

  // Check if subscription end date is in the future
  if (isSubscriptionDateActive(profile.subscription_end_date)) {
    return true;
  }
  
  // Fallback to legacy fields for backwards compatibility
  if (profile.plan_type === 'pro' || profile.plan_type === 'admin') {
    return true;
  }
  
  // Final fallback to is_premium field
  return profile.is_premium ?? false;
}

/**
 * Non-hook version for use outside of React components
 */
export function isUserProFromProfile(profile: {
  subscription_status?: SubscriptionStatus;
  plan_type?: string;
  is_premium?: boolean;
  subscription_end_date?: string | null;
} | null): boolean {
  if (!profile) return false;
  
  // Check new Stripe subscription status first
  if (isSubscriptionActive(profile.subscription_status ?? null)) {
    return true;
  }

  // Check if subscription end date is in the future
  if (isSubscriptionDateActive(profile.subscription_end_date)) {
    return true;
  }
  
  // Fallback to legacy fields
  if (profile.plan_type === 'pro' || profile.plan_type === 'admin') {
    return true;
  }
  
  return profile.is_premium ?? false;
}
